import { Container, Grid2 as Grid, Hidden, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import InstagramImageGallery from './InstagramImageGallery';

const instagramImagesSx = {
    gridContainer: {
        margin: 'auto',
        paddingTop: { xs: 0, sm: '40px' },
        paddingBottom: { xs: 0, sm: '40px' },
        justifyContent: 'center',
    },
    imageItem: {
        textAlign: 'center',
        padding: '10px',
    },
    image: {
        height: 260,
        width: 260,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        overflow: 'hidden',
        margin: '0 auto',
        borderRadius: '10px',
    },
    textArea: {
        padding: { xs: '20px', sm: '20px 0', md: '0 20px', lg: '0 40px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        maxWidth: 700,
    },
    imageArea: {
        justifyContent: { xs: 'center', sm: 'flex-end' },
        paddingLeft: { xs: 0, md: '10px' },
    },
    title: {
        fontSize: { xs: 22, sm: 28, md: 26, lg: 28 },
        color: 'primary.dark',
        lineHeight: '115%',
        letterSpacing: '-1px',
        fontWeight: 400,
        paddingBottom: { xs: '12px', md: '8px', lg: '12px' },
    },
    body: {
        fontSize: { xs: 16, md: 15, lg: 16 },
        lineHeight: { xs: '140%', md: '137%', lg: '140%' },
        opacity: 0.8,
        fontWeight: 400,
        paddingBottom: { xs: '20px', md: '12px', lg: '20px' },
        paddingTop: { xs: '10px', md: '8px', lg: '10px' },
    },
} as const;

export default function InstagramImages() {
    const [mediaList, setMediaList] = useState([]);
    const theme = useTheme();
    const xsScreen = useMediaQuery(theme.breakpoints.only('xs'));
    const path = import.meta.env.PROD ? '/api/' : 'http://localhost:5000/api/';

    const getMediaList = async () => {
        try {
            const res = await axios(`${path}instagram/images?entity=gslAviation`);
            const createList = isArray(res.data)
                ? res.data.map((url, i) => ({
                      url: url,
                      alt: `GSL Aviation scenic flight over the Great Barrier Reef and Tropical North Queensland - image no. ${i + 1}`,
                  }))
                : [];
            setMediaList(createList);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        getMediaList();
    }, []);

    return (
        <Container maxWidth="lg" style={{ padding: xsScreen && 0 }}>
            <Grid container sx={instagramImagesSx.gridContainer}>
                <Grid container sx={instagramImagesSx.imageArea} size={{ xs: 12, md: 5, lg: 6 }}>
                    <div style={{ maxWidth: 700, width: '100%', height: '100%' }}>
                        <InstagramImageGallery images={mediaList} />
                    </div>
                </Grid>
                <Grid size={{ xs: 12, md: 7, lg: 6 }} sx={instagramImagesSx.textArea}>
                    <Typography variant="h5" sx={instagramImagesSx.title}>
                        Beautiful North Queensland
                    </Typography>
                    <Typography sx={instagramImagesSx.body}>
                        Based in Cairns, GSL Aviation is lucky to explore some of the most amazing landscapes in
                        Australia, and we explore more of this region than any other operator. In Cairns, you&apos;ll
                        find secluded sand cays, and the Great Barrier Reef is right on your doorstep! We also fly
                        charter to the Whitsundays, home to one of Australia&apos;s most scenic archipelagos, with
                        fringing reef and beaches such as Whitehaven Beach. As well as our daily scenic flights, our
                        charter services take us all the way to the Torres Strait and the Gulf of Carpentaria.
                    </Typography>
                    <Hidden only="md">
                        <Typography sx={instagramImagesSx.body}>
                            Book with GSL Aviation, and see the best of North Queensland from above. Travel from Cairns
                            to the Great Barrier Reef, tropical islands, and the beautiful coast of Tropical North
                            Queensland.
                        </Typography>
                    </Hidden>
                    <Typography sx={instagramImagesSx.body}>
                        GSL Aviation also offer private charter throughout North Queensland and the Whitsundays, with
                        aircraft suited for a variety of missions, including government/corporate charter, as well as
                        bespoke tourism products.
                    </Typography>
                </Grid>
            </Grid>
        </Container>
    );
}

/*

Collate and shuffle all product images from Product Store

const productController = useContext(ProductsStore);
const mediaList = shuffle(
    productController.products.map((prod) => prod.photos).reduce((acc, photos) => acc.concat(photos))
).map((image) => image.largeURL);

*/
